import { useState } from "react";
import {
  ArrowRight,
  Bitcoin,
  Check,
  CheckCircle2,
  Copy,
  Loader2,
  Lock,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { useSubmitOrder } from "@/hooks/useSubmitOrder";
import { useToast } from "@/hooks/useToast";
import { useBchPrice, usdToBch } from "@/hooks/useBchPrice";
import { cn } from "@/lib/utils";
import {
  DEPOSIT_RATE,
  FEATURE_OPTIONS,
  PRICING_TIERS,
  SITE_TYPES,
  STYLE_OPTIONS,
  TIER_USD,
  type OrderPayload,
} from "@/lib/cashcraft";

const EMPTY: OrderPayload = {
  project: "",
  siteType: "landing",
  budget: "growth",
  pages: "",
  style: "modern",
  color: "#0AC18E",
  features: [],
  contact: "",
  bchAddress: "",
  details: "",
};

export function OrderForm() {
  const { mutateAsync, isPending } = useSubmitOrder();
  const { toast } = useToast();
  const { data: bchUsd } = useBchPrice();

  const [form, setForm] = useState<OrderPayload>(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [orderedTier, setOrderedTier] = useState<string>("growth");
  const [orderId, setOrderId] = useState<string>("");
  const [copied, setCopied] = useState(false);

  const trackUrl = orderId
    ? `${window.location.origin}/track/${orderId}`
    : "";

  const copyTrackUrl = async () => {
    try {
      await navigator.clipboard.writeText(trackUrl);
      setCopied(true);
      toast({ title: "Tracking link copied" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  };

  const set = <K extends keyof OrderPayload>(key: K, value: OrderPayload[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const toggleFeature = (value: string) =>
    setForm((f) => ({
      ...f,
      features: f.features.includes(value)
        ? f.features.filter((v) => v !== value)
        : [...f.features, value],
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.project.trim()) {
      toast({ title: "Please add a project name", variant: "destructive" });
      return;
    }
    try {
      const event = await mutateAsync(form);
      setOrderId(event.id);
      setOrderedTier(form.budget);
      setSubmitted(true);
      toast({ title: "Order sent!", description: "Now pay your deposit to lock it in." });
    } catch (err) {
      toast({
        title: "Couldn't send your order",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (submitted) {
    const tierUsd = TIER_USD[orderedTier] ?? 0;
    const depositUsd = Math.round(tierUsd * DEPOSIT_RATE);
    const depositBch =
      bchUsd && depositUsd ? usdToBch(depositUsd, bchUsd) : null;

    return (
      <Card className="border-primary/30 bg-card">
        <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/15">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-2xl font-bold">Your request is in! 🎉</h3>
          <p className="max-w-md text-muted-foreground">
            We received your project details securely. To lock in your spot and
            get us started, pay a{" "}
            <strong className="text-foreground">50% deposit</strong> now. The
            balance is due when your site is delivered.
          </p>

          <div className="w-full max-w-sm rounded-xl border border-primary/30 bg-primary/5 p-5">
            <div className="text-sm text-muted-foreground">
              Deposit to start
            </div>
            <div className="mt-1 flex items-baseline justify-center gap-2">
              <span className="text-3xl font-extrabold">
                {depositUsd ? `$${depositUsd}` : "—"}
              </span>
              {depositBch && (
                <span className="flex items-center gap-1 text-lg font-semibold text-primary">
                  <Bitcoin className="h-4 w-4" /> ≈ {depositBch} BCH
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              50% of the{" "}
              {PRICING_TIERS.find((t) => t.id === orderedTier)?.name ?? ""} plan
              {tierUsd ? ` ($${tierUsd})` : ""}
            </div>
          </div>

          <Button
            asChild
            size="lg"
            className="w-full max-w-sm rounded-full text-base font-semibold"
          >
            <Link to={`/pay?tier=${orderedTier}&deposit=1&order=${orderId}`}>
              Pay 50% deposit <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>

          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> Paid peer-to-peer in Bitcoin Cash.
          </p>

          {orderId && (
            <div className="w-full max-w-sm rounded-xl border border-border bg-muted/30 p-4 text-left">
              <p className="text-sm font-medium">Track your project</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Bookmark this private link to follow your build's progress.
              </p>
              <div className="mt-3 flex items-stretch gap-2">
                <code className="flex-1 truncate rounded-lg border border-border bg-background px-3 py-2 text-xs">
                  {trackUrl}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Copy tracking link"
                  onClick={copyTrackUrl}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <Button
                asChild
                variant="link"
                className="mt-1 h-auto p-0 text-xs text-primary"
              >
                <Link to={`/track/${orderId}`}>Open tracking page →</Link>
              </Button>
            </div>
          )}

          <Button
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => {
              setForm(EMPTY);
              setSubmitted(false);
              setOrderId("");
            }}
          >
            Submit another project
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/60 bg-card shadow-lg shadow-primary/5">
      <CardContent className="p-6 sm:p-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Project name */}
          <div className="space-y-2">
            <Label htmlFor="project">
              Project / business name <span className="text-primary">*</span>
            </Label>
            <Input
              id="project"
              placeholder="e.g. Bean & Brew Roastery"
              value={form.project}
              onChange={(e) => set("project", e.target.value)}
              required
            />
          </div>

          {/* Site type */}
          <div className="space-y-3">
            <Label>What kind of site do you need?</Label>
            <div className="grid gap-3 sm:grid-cols-2">
              {SITE_TYPES.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => set("siteType", opt.value)}
                  className={cn(
                    "rounded-xl border p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    form.siteType === opt.value
                      ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                      : "border-border hover:border-primary/50 hover:bg-accent/50",
                  )}
                >
                  <div className="font-semibold">{opt.label}</div>
                  <div className="mt-0.5 text-sm text-muted-foreground">
                    {opt.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Budget */}
          <div className="space-y-3">
            <Label>Budget / plan</Label>
            <div className="grid gap-3 sm:grid-cols-3">
              {PRICING_TIERS.map((tier) => (
                <button
                  type="button"
                  key={tier.id}
                  onClick={() => set("budget", tier.id)}
                  className={cn(
                    "rounded-xl border p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    form.budget === tier.id
                      ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                      : "border-border hover:border-primary/50 hover:bg-accent/50",
                  )}
                >
                  <div className="font-semibold">{tier.name}</div>
                  <div className="text-primary">{tier.priceUsd}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Style + pages */}
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-3">
              <Label>Visual style</Label>
              <div className="flex flex-wrap gap-2">
                {STYLE_OPTIONS.map((opt) => (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => set("style", opt.value)}
                    className={cn(
                      "rounded-full border px-4 py-2 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      form.style === opt.value
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <Label htmlFor="pages">Approx. number of pages</Label>
              <Input
                id="pages"
                inputMode="numeric"
                placeholder="e.g. 5"
                value={form.pages}
                onChange={(e) => set("pages", e.target.value)}
              />
              <div className="flex items-center gap-3 pt-1">
                <Label htmlFor="color" className="text-sm text-muted-foreground">
                  Accent color
                </Label>
                <input
                  id="color"
                  type="color"
                  value={form.color}
                  onChange={(e) => set("color", e.target.value)}
                  className="h-9 w-14 cursor-pointer rounded-md border border-border bg-transparent"
                  aria-label="Preferred accent color"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-3">
            <Label>Features you'd like</Label>
            <div className="grid gap-3 sm:grid-cols-2">
              {FEATURE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors",
                    form.features.includes(opt.value)
                      ? "border-primary/50 bg-primary/5"
                      : "border-border hover:bg-accent/40",
                  )}
                >
                  <Checkbox
                    checked={form.features.includes(opt.value)}
                    onCheckedChange={() => toggleFeature(opt.value)}
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2">
            <Label htmlFor="details">Tell us about your project</Label>
            <Textarea
              id="details"
              rows={5}
              placeholder="What's the goal of the site? Any pages, content, or references you have in mind?"
              value={form.details}
              onChange={(e) => set("details", e.target.value)}
            />
          </div>

          {/* Contact */}
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="contact">Best way to reach you</Label>
              <Input
                id="contact"
                placeholder="Email, Telegram, etc."
                value={form.contact}
                onChange={(e) => set("contact", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bch">Your BCH address (optional)</Label>
              <Input
                id="bch"
                placeholder="bitcoincash:q..."
                value={form.bchAddress}
                onChange={(e) => set("bchAddress", e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col items-center gap-3 pt-2">
            <Button
              type="submit"
              size="lg"
              disabled={isPending}
              className="w-full rounded-full text-base font-semibold sm:w-auto sm:px-12"
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                "Send my project request"
              )}
            </Button>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" /> No account needed. Your request is
              encrypted — only BCH Launch can read it.
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
